import React, { useState, useEffect } from 'react';
import {
    Container,
    Paper,
    Typography,
    Grid,
    TextField,
    Button,
    MenuItem,
    Alert,
    Card,
    CardContent,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Switch,
    FormControlLabel,
    Chip,
    Box,
    Divider,
    CircularProgress
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { fr } from 'date-fns/locale';
import CustomDatePicker from '../Calendar/CustomDatePicker';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DateRangeIcon from '@mui/icons-material/DateRange';
import api from '../../services/api';

interface Product {
    _id: string;
    name: string;
}

interface Slot {
    _id: string;
    time: string;
    capacity: number;
    isAvailable: boolean;
}

const AVAILABLE_TIMES = ['08:00', '08:30', '09:00', '09:30', '10:00', '10:30', '11:00', '13:30', '14:00', '14:30', '15:00', '15:30', '16:00', '17:30'];

const DAYS_OF_WEEK = [
    { value: 0, label: 'Dim' },
    { value: 1, label: 'Lun' },
    { value: 2, label: 'Mar' },
    { value: 3, label: 'Mer' },
    { value: 4, label: 'Jeu' },
    { value: 5, label: 'Ven' },
    { value: 6, label: 'Sam' },
];

const AdminPlanning: React.FC = () => {
    const [date, setDate] = useState<Date | null>(new Date());
    const [productId, setProductId] = useState<string>('');
    const [products, setProducts] = useState<Product[]>([]);
    const [slots, setSlots] = useState<Slot[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [newSlot, setNewSlot] = useState({ time: '09:00', capacity: 6 });

    // Mode édition
    const [editingSlot, setEditingSlot] = useState<Slot | null>(null);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editForm, setEditForm] = useState({ time: '', capacity: 6 });

    // Mode multi-dates
    const [multiDateMode, setMultiDateMode] = useState(false);
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const [selectedDays, setSelectedDays] = useState<number[]>([0, 1, 2, 3, 4, 5, 6]);
    const [selectedTimes, setSelectedTimes] = useState<string[]>([]);
    const [multiCapacity, setMultiCapacity] = useState(6);
    const [creatingMulti, setCreatingMulti] = useState(false);

    useEffect(() => {
        fetchProducts();
    }, []);

    useEffect(() => {
        if (date && productId && !multiDateMode) {
            fetchSlots();
        }
    }, [date, productId, multiDateMode]);

    const fetchProducts = async () => {
        try {
            const response = await api.get('/products');
            setProducts(response.data);
            if (response.data.length > 0) {
                setProductId(response.data[0]._id);
            }
        } catch (err) {
            setError('Erreur lors de la récupération des produits');
        }
    };

    const fetchSlots = async () => {
        if (!date || !productId) return;

        try {
            setLoading(true);
            const dateStr = formatDateStr(date);
            const response = await api.get(`/planning/slots?date=${dateStr}&productId=${productId}`);
            if (Array.isArray(response.data)) {
                setSlots(response.data);
            }
        } catch (err) {
            setError('Erreur lors de la récupération des créneaux');
        } finally {
            setLoading(false);
        }
    };

    const formatDateStr = (d: Date): string => {
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const handleOpenDialog = () => {
        setNewSlot({ time: '09:00', capacity: 6 });
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    const handleCreateSlot = async () => {
        if (!date || !productId) return;

        try {
            setLoading(true);
            await api.post('/planning/slots', {
                date: formatDateStr(date),
                time: newSlot.time,
                productId,
                capacity: newSlot.capacity
            });
            await fetchSlots();
            handleCloseDialog();
            setSuccess('Créneau créé avec succès');
        } catch (err) {
            setError('Erreur lors de la création du créneau');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteSlot = async (slotId: string) => {
        if (!date || !productId) return;

        try {
            await api.delete(`/planning/slots/${slotId}?date=${formatDateStr(date)}&productId=${productId}`);
            await fetchSlots();
            setSuccess('Créneau supprimé');
        } catch (err) {
            setError('Erreur lors de la suppression du créneau');
        }
    };

    const handleOpenEditDialog = (slot: Slot) => {
        setEditingSlot(slot);
        setEditForm({ time: slot.time, capacity: slot.capacity });
        setEditDialogOpen(true);
    };

    const handleCloseEditDialog = () => {
        setEditDialogOpen(false);
        setEditingSlot(null);
    };

    const handleUpdateSlot = async () => {
        if (!editingSlot) return;

        try {
            setLoading(true);
            await api.put(`/planning/slots/${editingSlot._id}`, {
                time: editForm.time,
                capacity: editForm.capacity
            });
            await fetchSlots();
            handleCloseEditDialog();
            setSuccess('Créneau modifié avec succès');
        } catch (err) {
            setError('Erreur lors de la modification du créneau');
        } finally {
            setLoading(false);
        }
    };

    const handleDayToggle = (day: number) => {
        setSelectedDays(prev =>
            prev.includes(day)
                ? prev.filter(d => d !== day)
                : [...prev, day].sort()
        );
    };

    const handleTimeToggle = (time: string) => {
        setSelectedTimes(prev =>
            prev.includes(time)
                ? prev.filter(t => t !== time)
                : [...prev, time].sort()
        );
    };

    const generateDatesFromRange = (): Date[] => {
        if (!startDate || !endDate) return [];

        const dates: Date[] = [];
        const current = new Date(startDate);
        current.setHours(0, 0, 0, 0);
        const end = new Date(endDate);
        end.setHours(0, 0, 0, 0);

        while (current <= end) {
            if (selectedDays.includes(current.getDay())) {
                dates.push(new Date(current));
            }
            current.setDate(current.getDate() + 1);
        }

        return dates;
    };

    const handleCreateMultiSlots = async () => {
        if (!productId || selectedTimes.length === 0) {
            setError('Veuillez sélectionner au moins un horaire');
            return;
        }

        const dates = generateDatesFromRange();
        if (dates.length === 0) {
            setError('Aucune date sélectionnée');
            return;
        }

        try {
            setCreatingMulti(true);
            setError(null);
            let createdCount = 0;
            let errorCount = 0;

            for (const d of dates) {
                for (const time of selectedTimes) {
                    try {
                        await api.post('/planning/slots', {
                            date: formatDateStr(d),
                            time: time,
                            productId,
                            capacity: multiCapacity
                        });
                        createdCount++;
                    } catch (err) {
                        errorCount++;
                    }
                }
            }

            if (createdCount > 0) {
                setSuccess(`${createdCount} créneau(x) créé(s) avec succès${errorCount > 0 ? ` (${errorCount} déjà existants)` : ''}`);
                // Reset
                setStartDate(null);
                setEndDate(null);
                setSelectedTimes([]);
            }
            if (errorCount > 0 && createdCount === 0) {
                setError('Tous les créneaux existent déjà');
            }
        } catch (err) {
            setError('Erreur lors de la création des créneaux');
        } finally {
            setCreatingMulti(false);
        }
    };

    const previewDates = generateDatesFromRange();
    const totalSlotsToCreate = previewDates.length * selectedTimes.length;

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                <Paper sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Typography variant="h4">
                            Gestion des créneaux
                        </Typography>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={multiDateMode}
                                    onChange={(e) => setMultiDateMode(e.target.checked)}
                                    color="primary"
                                />
                            }
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <DateRangeIcon />
                                    Mode multi-dates
                                </Box>
                            }
                        />
                    </Box>

                    {error && (
                        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>
                    )}
                    {success && (
                        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>{success}</Alert>
                    )}

                    <Grid container spacing={3}>
                        {/* Sélection du produit - toujours visible */}
                        <Grid item xs={12} md={multiDateMode ? 12 : 6}>
                            <TextField
                                select
                                label="Produit"
                                value={productId}
                                onChange={(e) => setProductId(e.target.value)}
                                fullWidth
                            >
                                <MenuItem value="">
                                    <em>Sélectionnez un produit</em>
                                </MenuItem>
                                {products.map((p) => (
                                    <MenuItem key={p._id} value={p._id}>
                                        {p.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        {!multiDateMode ? (
                            <>
                                {/* Mode simple - une date */}
                                <Grid item xs={12} md={6}>
                                    <CustomDatePicker
                                        label="Date"
                                        value={date}
                                        onChange={setDate}
                                        productId={productId}
                                        minDate={new Date()}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        startIcon={<AddIcon />}
                                        onClick={handleOpenDialog}
                                        disabled={loading || !productId}
                                    >
                                        Ajouter un créneau
                                    </Button>
                                </Grid>

                                {/* Liste des créneaux existants */}
                                <Grid item xs={12}>
                                    <Typography variant="h6" gutterBottom>
                                        Créneaux disponibles ({slots.length})
                                    </Typography>
                                    {loading ? (
                                        <CircularProgress />
                                    ) : slots.length === 0 ? (
                                        <Typography color="text.secondary">
                                            Aucun créneau pour cette date
                                        </Typography>
                                    ) : (
                                        <Grid container spacing={2}>
                                            {slots.map((slot) => (
                                                <Grid item xs={12} sm={6} md={4} key={slot._id}>
                                                    <Card sx={{ bgcolor: slot.isAvailable ? '#e8f5e9' : '#ffebee' }}>
                                                        <CardContent>
                                                            <Typography variant="h6">{slot.time}</Typography>
                                                            <Typography color="text.secondary">
                                                                Capacité: {slot.capacity}
                                                            </Typography>
                                                            <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                                                                <Button
                                                                    variant="contained"
                                                                    color="primary"
                                                                    size="small"
                                                                    startIcon={<EditIcon />}
                                                                    onClick={() => handleOpenEditDialog(slot)}
                                                                >
                                                                    Modifier
                                                                </Button>
                                                                <Button
                                                                    variant="contained"
                                                                    color="error"
                                                                    size="small"
                                                                    startIcon={<DeleteIcon />}
                                                                    onClick={() => handleDeleteSlot(slot._id)}
                                                                >
                                                                    Supprimer
                                                                </Button>
                                                            </Box>
                                                        </CardContent>
                                                    </Card>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    )}
                                </Grid>
                            </>
                        ) : (
                            <>
                                {/* Mode multi-dates */}
                                <Grid item xs={12}>
                                    <Divider sx={{ my: 2 }} />
                                    <Typography variant="h6" gutterBottom color="primary">
                                        1. Sélectionner la plage de dates
                                    </Typography>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <DatePicker
                                        label="Date de début"
                                        value={startDate}
                                        onChange={setStartDate}
                                        minDate={new Date()}
                                        format="dd/MM/yyyy"
                                        slotProps={{ textField: { fullWidth: true } }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <DatePicker
                                        label="Date de fin"
                                        value={endDate}
                                        onChange={setEndDate}
                                        minDate={startDate || new Date()}
                                        format="dd/MM/yyyy"
                                        slotProps={{ textField: { fullWidth: true } }}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2 }}>
                                        2. Filtrer par jours (optionnel)
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {DAYS_OF_WEEK.map((day) => (
                                            <Chip
                                                key={day.value}
                                                label={day.label}
                                                onClick={() => handleDayToggle(day.value)}
                                                color={selectedDays.includes(day.value) ? 'primary' : 'default'}
                                                variant={selectedDays.includes(day.value) ? 'filled' : 'outlined'}
                                            />
                                        ))}
                                        <Button size="small" onClick={() => setSelectedDays([0, 1, 2, 3, 4, 5, 6])}>
                                            Tous
                                        </Button>
                                        <Button size="small" onClick={() => setSelectedDays([])}>
                                            Aucun
                                        </Button>
                                    </Box>
                                </Grid>

                                <Grid item xs={12}>
                                    <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2 }}>
                                        3. Sélectionner les horaires
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {AVAILABLE_TIMES.map((time) => (
                                            <Chip
                                                key={time}
                                                label={time}
                                                onClick={() => handleTimeToggle(time)}
                                                color={selectedTimes.includes(time) ? 'secondary' : 'default'}
                                                variant={selectedTimes.includes(time) ? 'filled' : 'outlined'}
                                            />
                                        ))}
                                    </Box>
                                    {selectedTimes.length > 0 && (
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                            Horaires sélectionnés: {selectedTimes.join(', ')}
                                        </Typography>
                                    )}
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2 }}>
                                        4. Capacité par créneau
                                    </Typography>
                                    <TextField
                                        type="number"
                                        label="Nombre de places"
                                        value={multiCapacity}
                                        onChange={(e) => setMultiCapacity(parseInt(e.target.value) || 1)}
                                        inputProps={{ min: 1, max: 20 }}
                                        sx={{ width: 200 }}
                                    />
                                </Grid>

                                {/* Aperçu */}
                                {startDate && endDate && selectedTimes.length > 0 && (
                                    <Grid item xs={12}>
                                        <Paper elevation={2} sx={{ p: 2, bgcolor: '#e3f2fd', mt: 2 }}>
                                            <Typography variant="h6" gutterBottom color="primary">
                                                Aperçu
                                            </Typography>
                                            <Typography variant="body1">
                                                <strong>{previewDates.length}</strong> date(s) × <strong>{selectedTimes.length}</strong> horaire(s) = <strong>{totalSlotsToCreate}</strong> créneau(x) à créer
                                            </Typography>
                                            <Box sx={{ mt: 1, maxHeight: 150, overflowY: 'auto' }}>
                                                {previewDates.slice(0, 10).map((d, i) => (
                                                    <Chip
                                                        key={i}
                                                        label={d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}
                                                        size="small"
                                                        sx={{ m: 0.5 }}
                                                    />
                                                ))}
                                                {previewDates.length > 10 && (
                                                    <Chip label={`+${previewDates.length - 10} autres`} size="small" sx={{ m: 0.5 }} />
                                                )}
                                            </Box>
                                            <Button
                                                variant="contained"
                                                color="success"
                                                size="large"
                                                startIcon={creatingMulti ? <CircularProgress size={20} color="inherit" /> : <AddIcon />}
                                                onClick={handleCreateMultiSlots}
                                                disabled={creatingMulti || !productId}
                                                sx={{ mt: 2 }}
                                            >
                                                {creatingMulti ? 'Création en cours...' : `Créer ${totalSlotsToCreate} créneau(x)`}
                                            </Button>
                                        </Paper>
                                    </Grid>
                                )}
                            </>
                        )}
                    </Grid>
                </Paper>

                {/* Dialog pour créer un créneau simple */}
                <Dialog open={openDialog} onClose={handleCloseDialog}>
                    <DialogTitle>Ajouter un créneau</DialogTitle>
                    <DialogContent>
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                            <Grid item xs={12}>
                                <TextField
                                    select
                                    label="Heure"
                                    value={newSlot.time}
                                    onChange={(e) => setNewSlot({ ...newSlot, time: e.target.value })}
                                    fullWidth
                                >
                                    {AVAILABLE_TIMES.map((time) => (
                                        <MenuItem key={time} value={time}>
                                            {time}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    type="number"
                                    label="Capacité"
                                    value={newSlot.capacity}
                                    onChange={(e) => setNewSlot({ ...newSlot, capacity: parseInt(e.target.value) })}
                                    fullWidth
                                    inputProps={{ min: 1 }}
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDialog}>Annuler</Button>
                        <Button onClick={handleCreateSlot} variant="contained" disabled={loading}>
                            Créer
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Dialog pour modifier un créneau */}
                <Dialog open={editDialogOpen} onClose={handleCloseEditDialog}>
                    <DialogTitle>Modifier le créneau</DialogTitle>
                    <DialogContent>
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                            <Grid item xs={12}>
                                <TextField
                                    select
                                    label="Heure"
                                    value={editForm.time}
                                    onChange={(e) => setEditForm({ ...editForm, time: e.target.value })}
                                    fullWidth
                                >
                                    {AVAILABLE_TIMES.map((time) => (
                                        <MenuItem key={time} value={time}>
                                            {time}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    type="number"
                                    label="Capacité"
                                    value={editForm.capacity}
                                    onChange={(e) => setEditForm({ ...editForm, capacity: parseInt(e.target.value) })}
                                    fullWidth
                                    inputProps={{ min: 1 }}
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseEditDialog}>Annuler</Button>
                        <Button onClick={handleUpdateSlot} variant="contained" color="primary" disabled={loading}>
                            Enregistrer
                        </Button>
                    </DialogActions>
                </Dialog>
            </Container>
        </LocalizationProvider>
    );
};

export default AdminPlanning;
